import { TTLCache } from './cache';

const base = import.meta.env.VITE_API_BASE;

const KNOWN_SHORTENERS = ['bit.ly', 'tinyurl.com', 't.co', 'qrco.de', 'buff.ly', 'goo.gl', 'ow.ly', 'tiny.cc'];

// Per-signal timeouts (P3 harness): every network signal is individually
// bounded so one hung call degrades to "unknown" instead of blocking the verdict.
const RESOLVE_TIMEOUT_MS = 12_000; // server itself deadlines at 10s
const INTEL_TIMEOUT_MS = 6_500;
const DOMAIN_AGE_TIMEOUT_MS = 6_500;
const THREAT_INTEL_TIMEOUT_MS = 8_000; // server-side GSB/AbuseIPDB calls timeout at 6s

async function fetchWithTimeout(input: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export interface ResolveResponse {
  hops: string[];
  final: string;
  /** True when the chain may be incomplete (loop, timeout, hop cap, blocked). */
  partial?: boolean;
  reason?: string;
}

export interface ResolveAnalysisResponse {
  ok: boolean;
  analysis?: {
    input_url: string;
    redirect_chain: string[];
    resolved_url: string;
    hop_count: number;
    partial?: boolean;
    reason?: string;
  };
  error?: string;
}

export interface IntelResponse {
  urlhaus: {
    ok: boolean;
    source: string;
    query_status: string;
    matches: unknown[];
  } | null;
}

function validateResolveResponse(d: unknown): d is ResolveResponse {
  if (!d || typeof d !== 'object') return false;
  const data = d as Record<string, unknown>;
  return Array.isArray(data.hops) && typeof data.final === 'string';
}

export async function resolveChain(url: string): Promise<ResolveResponse> {
  if (!/^https?:/i.test(url)) {
    return { hops: [url], final: url };
  }

  // Try the new Netlify function first
  try {
    const netlifyResult = await resolveWithNetlifyFunction(url);
    if (netlifyResult) {
      return netlifyResult;
    }
  } catch (err) {
    console.warn('Netlify function resolution failed, falling back to local:', err);
  }

  // Fallback to existing logic
  if (!base) {
    return await resolveChainWithFallback(url);
  }
  try {
    const response = await fetchWithTimeout(`${base}/resolve?url=${encodeURIComponent(url)}`, {
      headers: { accept: 'application/json' }
    }, RESOLVE_TIMEOUT_MS);
    const data = await response.json();
    if (!validateResolveResponse(data)) throw new Error('Invalid API response');
    return data;
  } catch (err) {
    console.warn('API redirect check failed, falling back to enhanced local:', err);
    return await resolveChainWithFallback(url);
  }
}

async function resolveWithNetlifyFunction(url: string): Promise<ResolveResponse | null> {
  try {
    const response = await fetchWithTimeout('/api/resolve', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ url })
    }, RESOLVE_TIMEOUT_MS);

    if (!response.ok) {
      throw new Error(`Netlify function returned ${response.status}`);
    }

    const data: ResolveAnalysisResponse = await response.json();

    if (!data.ok || !data.analysis) {
      throw new Error(data.error || 'Invalid response from Netlify function');
    }

    return {
      hops: data.analysis.redirect_chain,
      final: data.analysis.resolved_url,
      partial: data.analysis.partial,
      reason: data.analysis.reason
    };
  } catch (err) {
    console.warn('Netlify function resolution failed:', err);
    return null;
  }
}

async function resolveChainWithFallback(url: string): Promise<ResolveResponse> {
  return await resolveChainLocally(url);
}

async function resolveChainLocally(url: string): Promise<ResolveResponse> {
  const hops: string[] = [url];
  let currentUrl = url;
  const maxRedirects = 10; // Prevent infinite redirect loops
  let redirectCount = 0;

  try {
    while (redirectCount < maxRedirects) {
      // Try HEAD first, fall back to GET if needed (some shorteners require GET)
      let response: Response;
      let locationHeader: string | null = null;

      try {
        response = await fetch(currentUrl, {
          method: 'HEAD',
          redirect: 'manual',
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        locationHeader = response.headers.get('location');
      } catch (headError) {
        console.debug('HEAD request failed, trying GET:', headError);
      }

      // If HEAD didn't give us a location, try GET
      if (!locationHeader) {
        try {
          response = await fetch(currentUrl, {
            method: 'GET',
            redirect: 'manual',
            signal: AbortSignal.timeout(5000)
          });
          locationHeader = response.headers.get('location');
        } catch (getError) {
          console.debug('GET request also failed:', getError);

          // For known shorteners, provide a helpful message about redirect expansion
          if (redirectCount === 0) {
            const urlObj = new URL(currentUrl);
            const domain = urlObj.hostname.toLowerCase();
            if (KNOWN_SHORTENERS.some(shortener => domain.includes(shortener))) {
              console.info(`Redirect expansion blocked by CORS for ${domain}. This is expected for some shorteners.`);
            }
          }
          break;
        }
      }

      if (!locationHeader) {
        // No more redirects
        break;
      }

      // Handle different types of redirects
      try {
        if (locationHeader.startsWith('http')) {
          currentUrl = locationHeader;
        } else {
          // Handle relative redirects
          currentUrl = new URL(locationHeader, currentUrl).href;
        }
      } catch (urlError) {
        console.warn('Invalid redirect URL:', locationHeader, urlError);
        break;
      }

      // Check if we've seen this URL before (redirect loop)
      if (hops.includes(currentUrl)) {
        console.warn('Redirect loop detected');
        break;
      }

      hops.push(currentUrl);
      redirectCount++;
    }
  } catch (err) {
    console.warn('Local redirect resolution failed:', err);
  }

  // If we couldn't resolve redirects but it's a known shortener, add a note
  if (hops.length === 1 && redirectCount === 0) {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.toLowerCase();
    if (KNOWN_SHORTENERS.some(shortener => domain.includes(shortener))) {
      console.info(`This is a shortened URL from ${domain}. Redirect expansion may be limited by browser security policies.`);
    }
  }

  return {
    hops,
    final: hops[hops.length - 1] || url
  };
}

export async function intel(url: string): Promise<IntelResponse> {
  try {
    // First try the live API lookup
    const response = await fetchWithTimeout('/.netlify/functions/intel-urlhaus', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ url })
    }, INTEL_TIMEOUT_MS);

    if (response.ok) {
      const data = await response.json();
      return { urlhaus: data };
    } else {
      console.warn('URLHaus API returned non-OK status:', response.status);
    }
  } catch (error) {
    console.warn('URLHaus live lookup failed:', error);
  }

  // Fallback to the local compact filter
  try {
    const { loadUrlhausBloom } = await import('./urlhaus');
    const filter = await loadUrlhausBloom();

    const hostname = new URL(url).hostname.toLowerCase();
    const isMalicious = filter.has(hostname);

    return {
      urlhaus: {
        ok: true,
        source: 'urlhaus',
        query_status: isMalicious ? 'found' : 'no_results',
        matches: isMalicious ? [{ url, threat: 'known_malicious_host' }] : []
      }
    };
  } catch (cacheError) {
    console.warn('Local URLHaus cache failed:', cacheError);
    return { urlhaus: null };
  }
}

/**
 * Domain age check result
 */
export interface DomainAgeResult {
  age_days: number | null;
  risk_points: number;
  message: string;
}

/**
 * Enhanced threat intelligence result
 */
export interface ThreatIntelResult {
  threat_detected: boolean;
  risk_points: number;
  message: string;
  threats: Array<{ source: string; details: string; score: number }>;
  sources_checked: string[];
}

/**
 * Combined threat intelligence results from all sources
 */
export interface AllThreatIntelResults {
  domainAge: DomainAgeResult | null;
  threatIntel: ThreatIntelResult | null;
}

// Registration dates don't change; cache determinate lookups for a day so
// re-scans of the same domain skip the network entirely.
const domainAgeCache = new TTLCache<DomainAgeResult>({
  dbName: 'qrcheck-domain-cache',
  storeName: 'domain-age',
  maxAgeMs: 24 * 60 * 60 * 1000,
  maxEntries: 200
});

/**
 * Check domain age via Netlify Function
 */
async function checkDomainAge(domain: string): Promise<DomainAgeResult> {
  const cacheKey = domain.toLowerCase();
  try {
    const cached = await domainAgeCache.get(cacheKey);
    if (cached) return cached;
  } catch (_e) {
    // Cache unavailable — fall through to the network
  }

  const response = await fetchWithTimeout('/.netlify/functions/check-domain-age', {
    method: 'POST',
    body: JSON.stringify({ domain }),
    headers: { 'Content-Type': 'application/json' }
  }, DOMAIN_AGE_TIMEOUT_MS);

  if (!response.ok) {
    throw new Error(`Domain age check failed: ${response.status}`);
  }

  const result: DomainAgeResult = await response.json();

  // Only cache determinate answers; "unknown" should retry next scan
  if (result && result.age_days !== null) {
    try {
      await domainAgeCache.set(cacheKey, result);
    } catch (_e) {
      // Cache write failure is non-fatal
    }
  }

  return result;
}

/**
 * Check enhanced threat intelligence via Netlify Function
 */
async function checkThreatIntel(domain: string, url: string): Promise<ThreatIntelResult> {
  const response = await fetchWithTimeout('/.netlify/functions/check-threat-intel', {
    method: 'POST',
    body: JSON.stringify({ domain, url }),
    headers: { 'Content-Type': 'application/json' }
  }, THREAT_INTEL_TIMEOUT_MS);

  if (!response.ok) {
    throw new Error(`Threat intel check failed: ${response.status}`);
  }

  const data = await response.json();

  // Normalize the response structure
  const normalizedThreats = Array.isArray(data.threats)
    ? data.threats.map((item: unknown) => ({
        source: String((item as Record<string, unknown>).source || (item as Record<string, unknown>)?.name || 'Unknown provider'),
        details: String((item as Record<string, unknown>).details || (item as Record<string, unknown>)?.message || 'Reported a potential threat'),
        score: typeof (item as Record<string, unknown>).score === 'number' ? (item as Record<string, unknown>).score as number : 0
      }))
    : [];

  const normalizedSources = Array.isArray(data.sources_checked)
    ? data.sources_checked.map((source: unknown) => String(source))
    : [];

  return {
    threat_detected: Boolean(data.threat_detected),
    risk_points: Number(data.risk_points) || 0,
    message: data.message || (data.threat_detected ? 'Threats detected' : 'No threats detected'),
    threats: normalizedThreats,
    sources_checked: normalizedSources
  };
}

/**
 * Check all threat intelligence sources in parallel
 * This dramatically improves performance by executing API calls concurrently
 * instead of sequentially.
 *
 * @param url - The URL to check
 * @returns Results from all threat intelligence sources (null if failed)
 */
export async function checkAllThreatIntel(url: string): Promise<AllThreatIntelResults> {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;

    // Execute both API calls in parallel
    const results = await Promise.allSettled([
      checkDomainAge(domain),
      checkThreatIntel(domain, url)
    ]);

    return {
      domainAge: results[0].status === 'fulfilled' ? results[0].value : null,
      threatIntel: results[1].status === 'fulfilled' ? results[1].value : null
    };
  } catch (error) {
    console.warn('Parallel threat intel check failed:', error);
    return {
      domainAge: null,
      threatIntel: null
    };
  }
}
